export type FilePickerOptions = {
  accept?: string;
  multiple?: boolean;
};

export class FilePickerError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FilePickerError";
  }
}

export class FilePickerCanceledError extends FilePickerError {
  constructor() {
    super("File selection canceled");
    this.name = "FilePickerCanceledError";
  }
}

export class FilePickerInvalidTypeError extends FilePickerError {
  constructor(file: File, accept: string) {
    super(`Invalid file type: "${file.type || file.name}". Allowed: ${accept}`);
    this.name = "FilePickerInvalidTypeError";
  }
}

export function useFilePicker() {
  const pickFile = ({
    accept = "",
    multiple = false,
  }: FilePickerOptions): Promise<File[]> => {
    return new Promise((resolve, reject) => {
      const input = document.createElement("input");

      input.type = "file";
      input.accept = accept;
      input.multiple = multiple;

      input.onchange = () => {
        try {
          if (!input.files || input.files.length === 0) {
            throw new FilePickerCanceledError();
          }

          const files = Array.from(input.files);

          if (accept) {
            const acceptRules = parseAccept(accept);

            for (const file of files) {
              if (!isFileAllowed(file, acceptRules)) {
                throw new FilePickerInvalidTypeError(file, accept);
              }
            }
          }

          resolve(files);
        } catch (err) {
          reject(err);
        } finally {
          input.remove();
        }
      };

      input.click();
    });
  };

  return { pickFile };
}

type AcceptRule = {
  mime?: string; // video/mp4
  wildcard?: string; // video/*
  extension?: string; // .mp4
};

function parseAccept(accept: string): AcceptRule[] {
  return accept
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean)
    .map((rule) => {
      if (rule.startsWith(".")) {
        return { extension: rule.toLowerCase() };
      }

      if (rule.endsWith("/*")) {
        return { wildcard: rule.slice(0, -2) };
      }

      return { mime: rule };
    });
}

function isFileAllowed(file: File, rules: AcceptRule[]): boolean {
  const fileType = file.type;
  const fileExt = "." + file.name.split(".").pop()?.toLowerCase();

  return rules.some((rule) => {
    if (rule.mime && fileType === rule.mime) return true;
    if (rule.wildcard && fileType.startsWith(rule.wildcard + "/")) return true;
    if (rule.extension && fileExt === rule.extension) return true;
    return false;
  });
}
